import React, { useEffect, useState } from "react";
import { Plus, Tag, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { getSegments, createSegment } from "../../lib/api";
import { useToast } from "../../contexts/ToastContext";
import { formatDateShort } from "../../lib/utils";

const schema = z.object({
  name: z.string().min(2, "Segment name required"),
  deviceCount: z.coerce.number().min(1, "Must have at least 1 device"),
});

export default function SegmentsPage() {
  const { toast } = useToast();
  const [segments, setSegments] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    getSegments().then(setSegments).catch(console.error);
  }, []);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      const seg = await createSegment({ ...data, createdAt: new Date().toISOString() });
      setSegments((prev) => [...prev, seg]);
      setOpen(false);
      reset();
      toast({ title: "Segment created", variant: "success" });
    } catch {
      toast({ title: "Failed to create segment", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Segments</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Fleet segments define the target audience for firmware rollouts.
            Segments bind to SNS / Pinpoint targeting and Step Functions phased rollout.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Segment
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Segment ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Devices</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {segments.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <code className="text-xs bg-slate-50 px-1.5 py-0.5 rounded text-slate-600">{s.id}</code>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-slate-400" />
                      <span className="font-medium text-sm">{s.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">{s.deviceCount.toLocaleString()}</TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDateShort(s.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Segment</DialogTitle>
            <DialogDescription>
              Create a device segment for targeting firmware rollouts.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="seg-name">Segment Name</Label>
              <Input id="seg-name" placeholder="e.g. ICU Units — West Campus" {...register("name")} />
              {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="seg-count">Device Count</Label>
              <Input id="seg-count" type="number" min={1} placeholder="500" {...register("deviceCount")} />
              {errors.deviceCount && <p className="text-xs text-red-600">{errors.deviceCount.message}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
